// src/pages/sessions/components/LiveSession/mobile/PhonePanel.jsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import SessionChat from '../Chat/SessionChat';
import PersonalChatInSession from '../Chat/PersonalChatInSession';
import EnhancedParticipantsList from '../EnhancedParticipantsList';
import BreakoutRoomPanel from '../breakout/BreakoutRoomPanel';

/**
 * PhonePanel
 *
 * Full-screen slide-up panel that renders when a non-video tab is active.
 * Uses the real desktop components directly — EnhancedParticipantsList,
 * BreakoutRoomPanel, SessionChat — not mobile re-implementations.
 *
 * The panel slides over the video area (which stays mounted underneath).
 * Audio is never interrupted by switching tabs.
 */

// Chat sub-panel with session/personal tabs
const ChatPanel = ({ sessionId, currentUser, onClose, onUnreadCountChange, isActive }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = React.useState('session');
  const [unreadPersonal, setUnreadPersonal] = React.useState(0);
  const [unreadSession, setUnreadSession] = React.useState(0);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'personal') setUnreadPersonal(0);
    else setUnreadSession(0);
  };

  const handleSessionUnread = (count) => {
    setUnreadSession(count);
    onUnreadCountChange?.(count + unreadPersonal);
  };

  const handlePersonalUnread = (count) => {
    setUnreadPersonal(count);
    onUnreadCountChange?.(count + unreadSession);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Tab bar */}
      <div className="flex flex-shrink-0 border-b border-gray-700">
        {['session', 'personal'].map(tab => (
          <button
            key={tab}
            onClick={() => handleTabChange(tab)}
            className={`flex-1 py-3 text-sm font-medium relative transition-colors
              ${activeTab === tab
                ? 'bg-gray-800 text-white border-b-2 border-cyan-500'
                : 'bg-gray-900 text-gray-400'
              }
            `}
          >
            {tab === 'session'
              ? t('liveSession.sidebar.sessionChat', 'Session chat')
              : t('chat.personal', 'Personal')}
            {tab === 'session' && activeTab !== 'session' && unreadSession > 0 && (
              <span className="absolute top-2 right-2 min-w-[16px] h-4 bg-red-600 rounded-full text-white text-[9px] flex items-center justify-center font-bold">
                {unreadSession}
              </span>
            )}
            {tab === 'personal' && activeTab !== 'personal' && unreadPersonal > 0 && (
              <span className="absolute top-2 right-2 min-w-[16px] h-4 bg-red-600 rounded-full text-white text-[9px] flex items-center justify-center font-bold">
                {unreadPersonal}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Chat content */}
      <div className="flex-1 relative overflow-hidden">
        <div className={`absolute inset-0 ${activeTab === 'session' ? 'block' : 'hidden'}`}>
          <SessionChat
            sessionId={sessionId}
            onUnreadCountChange={handleSessionUnread}
            isActive={isActive && activeTab === 'session'}
          />
        </div>
        <div className={`absolute inset-0 ${activeTab === 'personal' ? 'block' : 'hidden'}`}>
          <PersonalChatInSession
            isOpen
            sessionId={sessionId}
            currentUser={currentUser}
            onClose={onClose}
            onUnreadCountChange={handlePersonalUnread}
            isActive={isActive && activeTab === 'personal'}
          />
        </div>
      </div>
    </div>
  );
};

const PhonePanel = ({
  activeView,
  onViewChange,
  // Chat props
  sessionId,
  currentUser,
  onUnreadCountChange,
  // Participants props
  participantVirtualRooms,
  virtualRooms,
  teacherBroadcastRooms,
  teacherMonitorRooms,
  teacherMonitorSilentRooms,
  isTeacher,
  // Breakout props
  isAllBroadcasting,
  isAllMonitoring,
  isAllMonitoringSilent,
  onCreateRoom,
  onDeleteRoom,
  onRenameRoom,
  onAssignParticipant,
  onToggleBroadcast,
  onToggleMonitor,
  onToggleMonitorSilent,
  onToggleAllBroadcast,
  onToggleAllMonitor,
  onToggleAllMonitorSilent,
  onCreateIndividualRooms,
}) => {
  const { t } = useTranslation();
  const panelVisible = activeView !== 'video' && activeView !== 'whiteboard';

  const panelTitles = {
    chat: t('mobile.nav.chat', 'Chat'),
    people: t('mobile.nav.people', 'People'),
    rooms: t('mobile.nav.rooms', 'Rooms'),
  };

  return (
    <div
      className="absolute inset-0 bg-gray-900 flex flex-col"
      style={{
        zIndex: 20,
        transition: 'transform 250ms ease',
        transform: panelVisible ? 'translateY(0)' : 'translateY(100%)',
        pointerEvents: panelVisible ? 'auto' : 'none',
      }}
    >
      {/* Panel header with back button — safe area top padding here, not on root */}
      {panelVisible && (
        <div
          className="flex items-center gap-3 px-4 bg-gray-800 border-b border-gray-700 flex-shrink-0"
          style={{ paddingTop: 'calc(env(safe-area-inset-top) + 12px)', paddingBottom: 12 }}
        >
          <button
            onClick={() => onViewChange('video')}
            className="p-1.5 rounded-full bg-gray-700 hover:bg-gray-600 active:scale-95 transition-all flex-shrink-0"
            aria-label={t('common.back', 'Back')}
          >
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <span className="text-white font-semibold text-base">
            {panelTitles[activeView] || ''}
          </span>
        </div>
      )}
      {activeView === 'chat' && (
        <ChatPanel
          sessionId={sessionId}
          currentUser={currentUser}
          onClose={() => {}}
          onUnreadCountChange={onUnreadCountChange}
          isActive={activeView === 'chat'}
        />
      )}

      {activeView === 'people' && (
        <div className="flex-1 overflow-y-auto min-h-0 overscroll-contain" data-scrollable>
          <EnhancedParticipantsList
            participantVirtualRooms={participantVirtualRooms}
            virtualRooms={virtualRooms}
            teacherBroadcastRooms={teacherBroadcastRooms}
            teacherMonitorRooms={teacherMonitorRooms}
            teacherMonitorSilentRooms={teacherMonitorSilentRooms}
            isTeacher={isTeacher}
          />
        </div>
      )}

      {activeView === 'rooms' && isTeacher && (
        <div className="flex-1 overflow-y-auto min-h-0 overscroll-contain" data-scrollable>
          <BreakoutRoomPanel
          virtualRooms={virtualRooms}
          participantVirtualRooms={participantVirtualRooms}
          teacherBroadcastRooms={teacherBroadcastRooms}
          teacherMonitorRooms={teacherMonitorRooms}
          teacherMonitorSilentRooms={teacherMonitorSilentRooms}
          isAllBroadcasting={isAllBroadcasting}
          isAllMonitoring={isAllMonitoring}
          isAllMonitoringSilent={isAllMonitoringSilent}
          onCreateRoom={onCreateRoom}
          onDeleteRoom={onDeleteRoom}
          onRenameRoom={onRenameRoom}
          onAssignParticipant={onAssignParticipant}
          onToggleBroadcast={onToggleBroadcast}
          onToggleMonitor={onToggleMonitor}
          onToggleMonitorSilent={onToggleMonitorSilent}
          onToggleAllBroadcast={onToggleAllBroadcast}
          onToggleAllMonitor={onToggleAllMonitor}
          onToggleAllMonitorSilent={onToggleAllMonitorSilent}
          onCreateIndividualRooms={onCreateIndividualRooms}
        />
        </div>
      )}
    </div>
  );
};

export default PhonePanel;
